import { useQuery } from "@tanstack/react-query";
import { useRef, useEffect } from "react";
import { Product, ProductCategory, normalizeLanguage } from "./types";
import { BookingLanguage } from "@/types/booking";
import { getUrlParam } from "@/utils/urlUtils";
import {
  createProductsQueryFn,
  MembershipTier,
} from "@/hooks/createProductsQueryFn";
import { reportProductsFetchError } from "@/utils/errorReporting";
import { type MarketCountry, DEFAULT_MARKET } from "@/config/market";

export interface UseProductsResult {
  data?: {
    products: Product[];
    categories: ProductCategory[];
    membershipTiers?: MembershipTier[];
  };
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

// Global flag to track if error has been reported (singleton pattern)
// This ensures we only report once across ALL component instances
let globalErrorReported = false;

export const useProducts = (
  language: BookingLanguage = "en-US",
  market: MarketCountry = DEFAULT_MARKET,
): UseProductsResult => {
  // Enhanced development mode detection to include Lovable preview panel
  const isDevelopment =
    // Check hostname for local development or Lovable domains
    window.location.hostname.includes("localhost") ||
    window.location.hostname.includes("lovable.app") ||
    // Check if in iframe (Lovable preview panel)
    window !== window.parent ||
    // Check for development URL parameter
    getUrlParam("development") === "true";

  // Normalize language code using our utility function
  // This handles both 'de-DE' and shorter 'de' format from URL parameters
  const normalizedLanguage = normalizeLanguage(language);

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      "products",
      normalizedLanguage,
      market,
      isDevelopment ? "development" : "production",
    ],
    queryFn: async () => {
      console.log(
        `🔄 useProducts queryFn called. Environment detection: isFrame=${window !== window.parent}, hostname=${window.location.hostname}`,
      );

      // Use centralized query function for consistent product fetching
      const data = await createProductsQueryFn(
        normalizedLanguage,
        isDevelopment,
        market,
      )();

      console.log("✅ Products fetched successfully:", data?.products?.length);

      // Sort products by sort_order if present, otherwise by price in descending order
      return {
        products: (data?.products?.sort((a, b) => {
          // First sort by product_type (packages first)
          if (a.product_type === "package" && b.product_type !== "package")
            return -1;
          if (a.product_type !== "package" && b.product_type === "package")
            return 1;

          // Within the same product_type, sort by sort_order
          if (a.sort_order !== undefined && b.sort_order !== undefined) {
            return a.sort_order - b.sort_order;
          }

          // Otherwise fallback to price sorting (handle products without pricing data)
          const priceA = a.default_price?.unit_amount || 0;
          const priceB = b.default_price?.unit_amount || 0;
          return priceB - priceA;
        }) || []) as unknown as Product[],
        categories: (data?.categories as unknown as ProductCategory[]) || [],
        membershipTiers: (data?.membershipTiers ?? []).sort(
          (a, b) => a.sort_order - b.sort_order,
        ),
      };
    },
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 60,
    retry: 2, // React Query will retry up to 2 times (3 total attempts)
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s...
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    throwOnError: false, // Prevent errors from causing cascading re-renders
  });

  // Handle error reporting - only once globally across all component instances
  useEffect(() => {
    if (isError && error) {
      // Only log to console - no toast since we show ErrorFallback page
      if (!globalErrorReported) {
        console.error("🚨 Products fetch failed after all retries.", error);

        // Only report to webhook in production (not localhost or vercel.app preview)
        const hostname = window.location.hostname;
        const isProduction =
          !hostname.includes("localhost") && !hostname.includes("vercel.app");

        if (isProduction) {
          console.log("📞 Reporting error to webhook (once globally)...");
          globalErrorReported = true;

          reportProductsFetchError(error, {
            language: normalizedLanguage,
            isDevelopment,
            stage: "onDemand",
            retriesExhausted: true,
          })
            .then(() => {
              console.log("✅ Error reported to webhook successfully.");
            })
            .catch((err) => {
              console.error("❌ Failed to report error to webhook:", err);
            });
        } else {
          console.log(
            "🔕 Skipping webhook report (localhost or vercel.app preview)",
          );
          globalErrorReported = true;
        }
      }
    } else if (!isError && !isLoading) {
      // Reset the global guard when the query succeeds
      globalErrorReported = false;
    }
  }, [isError, error, isLoading, normalizedLanguage, isDevelopment]);

  return {
    data: data as unknown as UseProductsResult["data"],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};
